import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Badge, cn } from '@design-system';
import { SessionType } from '@shared';
import { selectFeatureFlags, selectSession } from '@client/model/selectors';
import { PlanBackButton } from '@/shared/components/plan-back-button';
import { useBackDestination } from '@/shared/hooks/use-back-destination';
import { CONSUMER_STAGES, CONSUMER_STEPS, type ConsumerStepId } from '../lib/steps';

// Guided planning's self-funding groups, finished before the hand-over, then the flow's own stages.
const GUIDED_GROUPS_DONE = ['Assets', 'Coverage Assessment'];
const FIRST_HANDED_OVER_STAGE = CONSUMER_STAGES.indexOf('Coverage');

interface GuidedJourneyHeaderProps {
  clientId: string;
  step: ConsumerStepId;
  actions?: ReactNode;
}

/** Guided planning's header (see `GuidedPlanningHeader`), drawn over the consumer flow it handed over to. */
export function GuidedJourneyHeader({ clientId, step, actions }: GuidedJourneyHeaderProps) {
  const { sessionType } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;
  // The same way out as guided planning's header (`useGuidedPlanningBack`).
  const back = useBackDestination(
    isD2C
      ? { label: 'Back to Summary', path: `/clients/${clientId}/onboarding/care-phases-summary` }
      : { label: 'Back to Financing Plan', path: `/clients/${clientId}/onboarding/funding-sources` },
  );
  const currentStage = CONSUMER_STEPS[step].stage;

  const segments = [
    ...GUIDED_GROUPS_DONE.map(name => ({ name, status: 'done' as const })),
    ...CONSUMER_STAGES.slice(FIRST_HANDED_OVER_STAGE).map((name, offset) => {
      const stage = FIRST_HANDED_OVER_STAGE + offset;
      return {
        name,
        status: stage < currentStage ? ('done' as const) : stage === currentStage ? ('current' as const) : ('upcoming' as const),
      };
    }),
  ];

  return (
    <div className="sticky top-0 z-20 border-b border-gray-200 bg-white">
      <div className="mx-auto max-w-5xl space-y-3 p-4 sm:space-y-4 sm:p-6">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2 text-sm">
            <Badge className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-100">
              Beta
            </Badge>
            <span className="truncate font-semibold text-gray-950">{CONSUMER_STEPS[step].kicker}</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <PlanBackButton label={back.label} onClick={back.goBack} testId="guided-journey-back" />
            {actions}
          </div>
        </div>
        <ol aria-label="Progress" className="flex items-center gap-2 sm:gap-4">
          {segments.map(({ name, status }) => (
            <li
              key={name}
              aria-label={name}
              aria-current={status === 'current' ? 'step' : undefined}
              className={cn('h-1 w-full rounded-full transition-colors duration-300', {
                'bg-primary': status === 'done',
                'bg-primary/50': status === 'current',
                'bg-gray-100': status === 'upcoming',
              })}
            />
          ))}
        </ol>
      </div>
    </div>
  );
}
